import { i18n } from '@lt/localization';
import { get, uniq, includes } from 'lodash';

import { formatDateToDayFull } from './date';

/**
 * Множественное число
 * @param {number} i
 * @param {string[]} variants
 * @returns {string}
 */
export const pluralize = (i, variants) => {
  let variant = 2;
  if (i % 10 === 1 && i % 100 !== 11) {
    variant = 0;
  }

  if (i % 10 >= 2 && i % 10 <= 4 && (i % 100 < 10 || i % 100 >= 20)) {
    variant = 1;
  }

  return variants[variant];
};

export const IMAGES_SIZE = {
  SMALL: 'smallImage',
  BIG: 'thumbImage',
  BIG_X2: 'thumbImageX2',
  FULL: 'fullImage',
};

export const IMAGES_TYPE = {
  BIG: 'topImages',
  SMALL: 'bottomImages',
};

export const isTouchDevice = () => {
  if (isSSR) return false;
  return 'ontouchstart' in document.documentElement;
};

export const makeTouchDependableEvents = () => {
  const touchable = isTouchDevice();
  return {
    mousedown: touchable ? 'TouchStart' : 'MouseDown',
    mousemove: touchable ? 'TouchMove' : 'MouseMove',
    mouseup: touchable ? 'TouchEnd' : 'MouseUp',
    click: 'Click',
    touch: 'TouchStart',
    wheel: touchable ? 'TouchMove' : 'Wheel',
    get(name, react = true) {
      return react ? `on${this[name]}` : this[name].toLowerCase();
    },
  };
};

export const unlessLocked = (ctx, evt, callback) => {
  if (!ctx.lock) {
    if (callback instanceof Function) callback();

    setTimeout(() => {
      ctx.lock = false;
    }, 50);
  } else {
    evt.preventDefault();
    evt.stopPropagation();
  }
};

export const getImageUrls = (img, config) => {
  const list = [img.images[config.src]];
  const srcset = config.srcSet || {};

  Object.keys(srcset).forEach(key => list.push(img.images[srcset[key]]));

  return uniq(list);
};

export const getFullUrl = (
  url: string | null | undefined,
  domain = 'level.travel'
): string | null => {
  if (!url) {
    return null;
  }
  return RegExp('http[s]?://').test(url) ? url : `//${domain + url}`;
};

/**
 * Возвращает значение не выходящее за min и max
 * @param {number} value
 * @param {number} min
 * @param {number} max
 * @return {number}
 */
export const numberRestrictor = (value, min = 0, max = 10) => {
  if (Number.isNaN(value)) {
    return 0;
  }

  if (value > max) {
    return max;
  }

  if (value < min) {
    return min;
  }

  return value;
};

export const formatDecimal = (value): string => {
  /**
   * Intl не везде доступен (то ли в nodejs у нас какая-то трабла толи в браузерах)
   * Вот задача в которой фиксил: https://leveltravel.atlassian.net/browse/LT-31774
   * Багов сыпалось очень много, так что не помешало бы разобраться
   * Но проверку не убирать для большей поддержки
   *
   * Лучше всего вынести либо @lt/localization, либо на худой случай в монолит
   */
  try {
    return Intl.NumberFormat('ru-RU', {
      style: 'decimal',
    }).format(value);
  } catch (err) {
    return `${value}`;
  }
};

export const formatDistance = value =>
  value > 999
    ? `${value / 1000} ${i18n.t('hotel.facts.distance_km')}`
    : `${value} ${i18n.t('commonPages.distanceM')}`;
/**
 * Проверяет, влезает ли элемент в контейнер
 * @param {*} htmlElement проверяемый элемент
 * @param {*} options проверять вертикально/горизонтально
 */
export const isElementOverflown = (
  htmlElement,
  options = { horizontal: false, vertical: true }
) => {
  const { clientWidth, clientHeight, scrollWidth, scrollHeight } = htmlElement;
  let result = false;
  if (options.vertical) {
    result = result || scrollHeight > clientHeight;
  }
  if (options.horizontal) {
    result = result || scrollWidth > clientWidth;
  }
  return result;
};

export const getOffersByNights = (offersForPansion, nightsCount) =>
  offersForPansion
    ? offersForPansion.filter(offer => offer.nights_count === nightsCount)
    : [];

export const getOffersStartDate = offersForPansion =>
  offersForPansion ? offersForPansion.start_date : '';

/**
 * Создает текстовую строку вида "19 сентября на 10 ночей"
 * @param {object} offers
 * @returns {string}
 */
export const makeHeaderTitle = offers => {
  const [offer] = offers;
  const { nights_count: nights, start_date: date } = offer;
  const dateText = formatDateToDayFull(date);
  const nightsText = pluralize(nights, [
    i18n.t('hotel.offer.night', { count: 0 }),
    i18n.t('hotel.offer.night', { count: 1 }),
    i18n.t('hotel.offer.night', { count: 2 }),
  ]);
  return `${dateText} ${i18n.t(
    'filters.prices.title_on'
  )} ${nights} ${nightsText}`;
};

/**
 * Добавляет в offer.included информацию об отсуствии доп кровати
 * @return {Array}
 */
export const makeIncludedServices = offer => {
  const withoutExtraBed = get(offer, ['misc_data', 'extra_bed']) === false;
  const included: Array<{ code_name: string; name: string }> = [];

  if (withoutExtraBed) {
    included.push({
      code_name: 'without_extra_bed',
      name: i18n.t('hotel.offer.extraBed'),
    });
  }

  return [...included, ...(get(offer, ['extras', 'included']) || [])];
};

export const preventEvent = e => {
  e.preventDefault();
  e.stopPropagation();
};

const FREE_CANCELLATION_KINDS = ['flexible'];
export const isFreeCancellation = cancellationPolicy =>
  includes(FREE_CANCELLATION_KINDS, get(cancellationPolicy, 'kind'));

/**
 * SSR ли окружение
 */
export const isSSR = (function () {
  try {
    return !(document !== undefined);
  } catch (e) {
    return true;
  }
})();
